import type { TeamCompetition, MemberSummary, CurrentRyderCupTeamMember } from '../types/api.types';

export interface CaptaincyRecord {
  memberId: string;
  memberName: string;
  timesAsCaptain: number;
  captaincies: Array<{
    year: number;
    team: string;
    won: boolean;
  }>;
  appearancesSinceLastCaptaincy: number;
  appearancesSinceLastCaptaincyIncludingUpcoming: number;
  isCurrentCaptain: boolean;
  isInCurrentTeam: boolean;
}

/**
 * Sorts competitions chronologically (oldest first)
 */
const sortCompetitionsByDate = (competitions: TeamCompetition[]): TeamCompetition[] => {
  return [...competitions].sort((a, b) => {
    const dateA = new Date(a.eventDate);
    const dateB = new Date(b.eventDate);
    return dateA.getTime() - dateB.getTime();
  });
};

/**
 * Initializes the captaincy map with all members
 */
const initializeCaptaincyMap = (members: MemberSummary[]): Map<string, CaptaincyRecord> => {
  const map = new Map<string, CaptaincyRecord>();
  members.forEach((member) => {
    map.set(member.id, {
      memberId: member.id,
      memberName: `${member.firstName} ${member.surname}`,
      timesAsCaptain: 0,
      captaincies: [],
      appearancesSinceLastCaptaincy: 0,
      appearancesSinceLastCaptaincyIncludingUpcoming: 0,
      isCurrentCaptain: false,
      isInCurrentTeam: false,
    });
  });
  return map;
};

/**
 * Processes captaincy information from competitions
 */
const processCaptaincies = (
  sortedCompetitions: TeamCompetition[],
  memberCaptaincyMap: Map<string, CaptaincyRecord>,
  lastCaptaincyDate: Map<string, Date>,
  firstAppearanceDate: Map<string, Date>
): void => {
  sortedCompetitions.forEach((comp) => {
    const compDate = new Date(comp.eventDate);
    const compYear = compDate.getFullYear();

    // Track first appearance for all competitors
    if (comp.competitors) {
      comp.competitors.forEach((competitor) => {
        if (!firstAppearanceDate.has(competitor.memberID)) {
          firstAppearanceDate.set(competitor.memberID, compDate);
        }
      });
    }

    // Process team A captain
    if (comp.teamACaptainID) {
      const record = memberCaptaincyMap.get(comp.teamACaptainID);
      if (record) {
        record.timesAsCaptain++;
        record.captaincies.push({
          year: compYear,
          team: comp.teamA,
          won: comp.winner === comp.teamA,
        });
        lastCaptaincyDate.set(comp.teamACaptainID, compDate);
      }
    }

    // Process team B captain
    if (comp.teamBCaptainID) {
      const record = memberCaptaincyMap.get(comp.teamBCaptainID);
      if (record) {
        record.timesAsCaptain++;
        record.captaincies.push({
          year: compYear,
          team: comp.teamB,
          won: comp.winner === comp.teamB,
        });
        lastCaptaincyDate.set(comp.teamBCaptainID, compDate);
      }
    }
  });
};

/**
 * Calculates appearances since last captaincy for each member
 */
const calculateAppearancesSince = (params: {
  memberCaptaincyMap: Map<string, CaptaincyRecord>;
  sortedCompetitions: TeamCompetition[];
  lastCaptaincyDate: Map<string, Date>;
  firstAppearanceDate: Map<string, Date>;
  currentCaptainIds: Set<string>;
  currentTeamMemberIds: Set<string>;
  hasUpcomingEvent: boolean;
}): void => {
  const {
    memberCaptaincyMap,
    sortedCompetitions,
    lastCaptaincyDate,
    firstAppearanceDate,
    currentCaptainIds,
    currentTeamMemberIds,
    hasUpcomingEvent,
  } = params;

  memberCaptaincyMap.forEach((record, memberId) => {
    const referenceDate = lastCaptaincyDate.get(memberId) || firstAppearanceDate.get(memberId);

    // Mark if this member is a current captain
    record.isCurrentCaptain = currentCaptainIds.has(memberId);

    // Mark if this member is in the current team
    record.isInCurrentTeam = currentTeamMemberIds.has(memberId);

    if (referenceDate) {
      // Count actual appearances (in competitors list) after the reference date
      const appearancesSince = sortedCompetitions.filter((comp) => {
        const compDate = new Date(comp.eventDate);
        if (compDate <= referenceDate) return false;

        // Check if member actually appeared in this competition
        return comp.competitors?.some((competitor) => competitor.memberID === memberId) || false;
      }).length;

      // Store the base count (without upcoming event)
      record.appearancesSinceLastCaptaincy = appearancesSince;

      // For "Due Captaincy" mode, also store count including upcoming event
      record.appearancesSinceLastCaptaincyIncludingUpcoming =
        appearancesSince + (record.isInCurrentTeam && hasUpcomingEvent ? 1 : 0);
    }
  });
};

/**
 * Sorts captaincy records by number of captaincies and name
 */
const sortCaptaincyRecords = (records: CaptaincyRecord[]): CaptaincyRecord[] => {
  return records.sort((a, b) => {
    if (b.timesAsCaptain !== a.timesAsCaptain) {
      return b.timesAsCaptain - a.timesAsCaptain;
    }
    return a.memberName.localeCompare(b.memberName);
  });
};

/**
 * Main function to calculate captaincy statistics
 */
export const calculateCaptaincyStatistics = (
  teamCompetitions: TeamCompetition[],
  members: MemberSummary[],
  ryderCupTeams: CurrentRyderCupTeamMember[]
): CaptaincyRecord[] => {
  // Initialize data structures
  const memberCaptaincyMap = initializeCaptaincyMap(members);
  const lastCaptaincyDate = new Map<string, Date>();
  const firstAppearanceDate = new Map<string, Date>();

  // Sort competitions chronologically
  const sortedCompetitions = sortCompetitionsByDate(teamCompetitions);

  // Identify current team members and captains
  const currentTeamMemberIds = new Set(ryderCupTeams.map((member) => member.id));
  const currentCaptainIds = new Set(
    ryderCupTeams.filter((member) => member.captain === 'Yes').map((member) => member.id)
  );

  // Process captaincies from competitions
  processCaptaincies(sortedCompetitions, memberCaptaincyMap, lastCaptaincyDate, firstAppearanceDate);

  // Calculate appearances since last captaincy
  calculateAppearancesSince({
    memberCaptaincyMap,
    sortedCompetitions,
    lastCaptaincyDate,
    firstAppearanceDate,
    currentCaptainIds,
    currentTeamMemberIds,
    hasUpcomingEvent: ryderCupTeams.length > 0,
  });

  // Convert map to array, filter captains only, and sort
  const records = Array.from(memberCaptaincyMap.values()).filter((record) => record.timesAsCaptain > 0);

  return sortCaptaincyRecords(records);
};

/**
 * Calculate win/loss record from captaincies
 *
 * @param captaincies - Array of captaincy entries
 * @returns Object with wins and losses counts
 */
export const calculateCaptaincyRecord = (
  captaincies: Array<{ year: number; team: string; won: boolean }>
): { wins: number; losses: number } => {
  const wins = captaincies.filter((c) => c.won).length;
  const losses = captaincies.length - wins;
  return { wins, losses };
};
